import type { TasksKanbanLogsFilterParams } from '../model/interfaces'
import type { KanbanLog } from '@entities/kanban-comment'
import type { Pagination } from '@shared/config'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useTasksKanbanStore } from '../model/store'

interface TasksKanbanLogsApiOptions {
  id: string | number
  params?: TasksKanbanLogsFilterParams
  cursor?: string
}

interface TasksKanbanLogsApiResponse {
  logs: KanbanLog[]
  meta: Pagination
}

const buildQueryParams = (params?: TasksKanbanLogsFilterParams): Record<string, string> => {
  if (!params) return {}

  const query: Record<string, string> = {}

  if (params.query) query.query = params.query
  if (params.date_from) query.date_from = params.date_from
  if (params.date_to) query.date_to = params.date_to

  return query
}

export const useTasksKanbanLogsApi = async (options: TasksKanbanLogsApiOptions): Promise<KanbanLog[]> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const isLoadMore = !!options.cursor

  const url = useAdminRoutes('admin.kanban-tasks.logs', {
    id: options.id,
    ...buildQueryParams(options.params),
    ...(options.cursor ? { cursor: options.cursor } : {}),
  })

  const tasksKanbanStore = useTasksKanbanStore()
  const commonStore = useCommonStore()

  if (!isLoadMore) commonStore.setLoading(true)

  try {
    const { data } = await $axios.get(url!)
    const response: TasksKanbanLogsApiResponse = data.data

    if (isLoadMore) {
      tasksKanbanStore.appendLogs(response.logs ?? [])
    } else {
      tasksKanbanStore.setLogs(response.logs ?? [])
    }
    tasksKanbanStore.setLogsPagination(response.meta)

    if (!isLoadMore) commonStore.setLoading(false)

    return response.logs ?? []
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    if (!isLoadMore) commonStore.setLoading(false)

    return []
  }
}
